import { z } from "zod";

/**
 * Shared DTO cho admin login + 2FA (US-AE1.F1-0001 / @UC9001).
 * Một nguồn sự thật cho api (S01 — validate biên + phát hành pending/session) và
 * admin web (S03 — gọi + map lỗi), tránh drift (DRY api↔admin).
 *
 * BẢO MẬT (HARD invariants):
 * - Login KHÔNG enforce policy độ dài password (đó là của register). Login chỉ
 *   yêu cầu password hiện diện (min 1) — enumeration-safe: ngoài-allowlist và
 *   sai-password đều trả CÙNG một mã `INVALID_CREDENTIALS` (AC4), không lộ tồn tại.
 * - Response login KHÔNG mang access/refresh token — ép invariant "2FA-trước-session"
 *   ở tầng type (AC3). Chỉ có `pendingToken` để bước 2FA tiếp tục.
 * - Không nhúng giá trị tài chính/secret trong contracts.
 */

/** Bước 1: đăng nhập email + password → trả challenge "pending 2FA". */
export const adminLoginRequestSchema = z.object({
  // Chuẩn hoá tại biên: trim + lowercase để khớp citext @unique, nhất quán với
  // convention end-user (auth-login.ts).
  email: z.string().trim().toLowerCase().pipe(z.string().email()),
  password: z.string().min(1),
});

export type AdminLoginRequest = z.infer<typeof adminLoginRequestSchema>;

/**
 * Response của bước 1 (login). AC3: CHỈ pending — TUYỆT ĐỐI không chứa
 * `token`/`accessToken`/`refreshToken`. Session chỉ phát hành sau khi 2FA pass.
 */
export interface AdminLoginResponse {
  /** Tham chiếu phiên chờ 2FA (ngắn hạn), client gửi lại ở bước 2. */
  pendingToken: string;
  /** Báo client cần render Turnstile trước khi thử lại (khi bị nghi ngờ/khoá tạm). */
  requiresTurnstile?: boolean;
}

/**
 * Bước 2: xác thực 2FA. Cơ chế 2FA đã chốt = TOTP (index §6.1, DoR 26/06):
 * `code` là mã TOTP 6 số HOẶC recovery code. Giữ tối thiểu `min(1)` để chấp nhận
 * cả hai dạng — định dạng chi tiết do backend (S01) kiểm.
 */
export const admin2faRequestSchema = z.object({
  pendingToken: z.string().min(1),
  code: z.string().min(1),
});

export type Admin2faRequest = z.infer<typeof admin2faRequestSchema>;

/**
 * Response của bước 2 (2FA pass) → phát hành session.
 * `token` = access token trong body. Refresh token là httpOnly cookie do server
 * set (Set-Cookie), KHÔNG nằm trong body — không lộ ra JS (C3).
 */
export interface Admin2faResponse {
  token: string;
  admin: {
    id: string;
    email: string;
    role: string;
  };
}

/**
 * US-AE-IMP.F3-0143 (@UC9150) — nhánh response bước 2FA khi admin CHƯA có TOTP đã-confirm
 * (mới cấp HOẶC vừa bị SUPER_ADMIN reset 2FA). Thay vì 401 mù (deadlock), server cấp một
 * enrollment-ticket ngắn hạn single-use để admin enroll 2FA (KHÔNG cấp session ở bước này).
 *
 * BẢO MẬT: nhánh này TUYỆT ĐỐI KHÔNG mang `token`/session — chỉ `enrollTicket` opaque (KHÔNG
 * nhúng TOTP secret). `requiresEnrollment: true` là discriminant để client narrow union.
 */
export interface Admin2faEnrollmentRequired {
  requiresEnrollment: true;
  /** Ticket opaque single-use (DB-backed, TTL ngắn) để gọi enroll-ticket endpoints. */
  enrollTicket: string;
}

/**
 * Kết quả bước 2FA = UNION: hoặc session đầy đủ (`Admin2faResponse`) khi admin đã có TOTP,
 * hoặc "cần enroll" (`Admin2faEnrollmentRequired`) khi chưa. Client dùng `isEnrollmentRequired`
 * để phân biệt (discriminant `requiresEnrollment`).
 */
export type Admin2faResult = Admin2faResponse | Admin2faEnrollmentRequired;

/** Type-guard: `true` nếu kết quả 2FA là nhánh "cần enroll" (narrow sang Admin2faEnrollmentRequired). */
export function isEnrollmentRequired(
  result: Admin2faResult,
): result is Admin2faEnrollmentRequired {
  return "requiresEnrollment" in result && result.requiresEnrollment === true;
}

/**
 * Hằng message/code lỗi admin auth dùng chung (DRY api↔admin).
 * Message enumeration-safe: ngoài-allowlist và sai-password đều dùng
 * INVALID_CREDENTIALS (AC4) — không có mã riêng "USER_NOT_FOUND".
 */
export const ADMIN_LOGIN_INVALID_CREDENTIALS_MESSAGE = "Email hoặc mật khẩu không đúng";
export const ADMIN_LOGIN_LOCKED_MESSAGE = "Tài khoản bị khoá tạm thời. Vui lòng thử lại sau.";
export const ADMIN_2FA_INVALID_MESSAGE = "Mã xác thực không đúng hoặc đã hết hạn";

/** Machine code ổn định, gắn vào RFC7807 problem+json `code`. */
export const ADMIN_LOGIN_INVALID_CREDENTIALS_CODE = "INVALID_CREDENTIALS";
export const ADMIN_LOGIN_LOCKED_CODE = "ACCOUNT_LOCKED";
export const ADMIN_2FA_INVALID_CODE = "INVALID_2FA";
